import { useEffect, useMemo, useState } from "react";
import { Building2, Calculator, FileText, Search } from "lucide-react";
import { http, loanTypes, money, notify } from "../api";
import { Loading, PanelHead } from "./common";

/**
 * Policy Finder — Sales Associate & Manager tool.
 *  1. Pick a Company (typeahead from companies master) — auto-loads category & suggested bank.
 *  2. Pick a Loan Type — filters applicable policies.
 *  3. Shows every eligible policy card (eligibility ranges + required docs from Bank).
 *  4. Inline EMI Calculator on each card.
 */
export default function PolicyFinder() {
  const [q, setQ] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loanType, setLoanType] = useState("");
  const [banks, setBanks] = useState([]);
  const [bankFilter, setBankFilter] = useState("");
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    http.get("/banks").then((r) => setBanks(r.data || [])).catch(() => {});
  }, []);

  // Company typeahead
  useEffect(() => {
    if (!q.trim()) { setCompanies([]); return; }
    const h = setTimeout(() => {
      http.get(`/companies/search?q=${encodeURIComponent(q.trim())}&limit=10`)
        .then((r) => setCompanies(r.data.companies || []))
        .catch(() => setCompanies([]));
    }, 200);
    return () => clearTimeout(h);
  }, [q]);

  const runSearch = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (loanType) params.set("loan_type", loanType);
    if (bankFilter) params.set("bank_id", bankFilter);
    // Category derives from the company's lender-specific mappings.
    if (selectedCompany?.mappings?.length) {
      const cats = new Set();
      for (const m of selectedCompany.mappings) {
        if (bankFilter && m.bank_id !== bankFilter) continue;
        if (loanType && (m.product_name || "").toLowerCase() !== loanType.toLowerCase()
            && !(m.product_name || "").toLowerCase().includes(loanType.toLowerCase())) continue;
        if (m.category) cats.add(m.category);
      }
      // If exactly one applicable category, filter; otherwise no filter (widest net).
      if (cats.size === 1) params.set("category", [...cats][0]);
    }
    params.set("limit", "50");
    http.get(`/policies/search?${params.toString()}`)
      .then((r) => setPolicies(r.data.policies || []))
      .catch((err) => notify.error(err, "Could not load policies"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Auto search whenever filters change AND user picked at least a company or loan type
    if (selectedCompany || loanType || bankFilter) runSearch();
    else setPolicies([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany?.company_id, loanType, bankFilter]);

  const pickCompany = (c) => {
    setSelectedCompany(c);
    setQ(c.name);
    setCompanies([]);
    // Auto-focus the bank filter to the first mapping's bank for convenience.
    if (c.mappings && c.mappings.length === 1) setBankFilter(c.mappings[0].bank_id);
  };

  const clearAll = () => {
    setSelectedCompany(null);
    setQ("");
    setLoanType("");
    setBankFilter("");
    setPolicies([]);
  };

  return (
    <section data-testid="policy-finder-panel">
      <PanelHead
        title="Policy Finder"
        subtitle="Find every bank / NBFC policy that fits your customer's profile — with eligibility ranges, required documents and an inline EMI calculator."
      />

      <div className="policy-finder-controls" style={{
        display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto",
        gap: 12, alignItems: "end", margin: "16px 0",
      }}>
        <div style={{ position: "relative" }}>
          <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>
            Customer's employer / company
          </label>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }} />
            <input
              data-testid="finder-company-search"
              value={q}
              placeholder="Start typing a company name…"
              onChange={(e) => { setQ(e.target.value); setSelectedCompany(null); }}
              style={{
                width: "100%", padding: "8px 12px 8px 32px",
                border: "1px solid var(--border)", background: "var(--surface)",
              }}
            />
          </div>
          {companies.length > 0 && !selectedCompany && (
            <ul
              data-testid="finder-company-dropdown"
              style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                background: "var(--surface)", border: "1px solid var(--border)",
                margin: 0, padding: 0, listStyle: "none", maxHeight: 260, overflow: "auto",
              }}
            >
              {companies.map((c) => (
                <li key={c.company_id}>
                  <button
                    type="button"
                    data-testid={`finder-company-option-${c.company_id}`}
                    onClick={() => pickCompany(c)}
                    style={{
                      width: "100%", textAlign: "left", padding: "8px 12px",
                      background: "transparent", border: "none", cursor: "pointer",
                      borderTop: "1px solid var(--border)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{c.name}</span>
                      <small className="muted">{(c.mappings || []).length} mapping{(c.mappings || []).length === 1 ? "" : "s"}</small>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Loan type</label>
          <select
            data-testid="finder-loantype"
            value={loanType}
            onChange={(e) => setLoanType(e.target.value)}
            style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--border)", background: "var(--surface)" }}
          >
            <option value="">All loan types</option>
            {loanTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>Bank / NBFC</label>
          <select
            data-testid="finder-bank"
            value={bankFilter}
            onChange={(e) => setBankFilter(e.target.value)}
            style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--border)", background: "var(--surface)" }}
          >
            <option value="">Any</option>
            {banks.filter((b) => b.active !== false).map((b) => (
              <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
            ))}
          </select>
        </div>

        <button className="outline-button" onClick={clearAll} data-testid="finder-clear">
          Clear
        </button>
      </div>

      {selectedCompany && (
        <div
          data-testid="finder-company-summary"
          style={{
            display: "flex", gap: 16, alignItems: "center", padding: 12,
            background: "var(--surface)", border: "1px solid var(--border)", marginBottom: 12,
          }}
        >
          <Building2 size={18} />
          <div>
            <strong>{selectedCompany.name}</strong>
            <div className="muted" style={{ fontSize: 12 }}>
              Mappings: <span data-testid="finder-company-category">
                {(selectedCompany.mappings || []).length === 0
                  ? "—"
                  : `${(selectedCompany.mappings || []).length} lender${(selectedCompany.mappings || []).length === 1 ? "" : "s"}`}
              </span>
            </div>
          </div>
        </div>
      )}

      {loading && <Loading>Finding matching policies…</Loading>}

      {!loading && policies.length === 0 && (selectedCompany || loanType || bankFilter) && (
        <div className="muted" data-testid="finder-empty" style={{ padding: 24, textAlign: "center" }}>
          No policies match these filters yet.
        </div>
      )}

      {!loading && policies.length === 0 && !selectedCompany && !loanType && !bankFilter && (
        <div className="muted" data-testid="finder-idle" style={{ padding: 24, textAlign: "center" }}>
          Pick a company or loan type to see matching policies.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}>
        {policies.map((p) => (
          <PolicyCard key={p.policy_id} policy={p} />
        ))}
      </div>
    </section>
  );
}

function PolicyCard({ policy }) {
  const [detail, setDetail] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadDetail = () => {
    if (detail) { setExpanded((x) => !x); return; }
    http.get(`/policies/${policy.policy_id}`)
      .then((r) => { setDetail(r.data); setExpanded(true); })
      .catch((err) => notify.error(err, "Could not load policy details"));
  };

  const midRoi = ((Number(policy.roi_min) + Number(policy.roi_max)) / 2) || Number(policy.roi_min) || 0;
  const midTenure = Math.max(Number(policy.tenure_min_months) || 12, 12);

  return (
    <div
      data-testid={`policy-card-${policy.policy_id}`}
      style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        padding: 16, display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <strong style={{ fontSize: 15 }}>{policy.bank_name}</strong>
          <div className="muted" style={{ fontSize: 12 }}>
            {policy.product_name}
            {policy.loan_type && policy.loan_type !== policy.product_name ? ` · ${policy.loan_type}` : ""}
          </div>
        </div>
        <span className="chip chip-green">Active</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12, marginTop: 4 }}>
        <div><span className="muted">Min income</span><br /><strong>{policy.min_income ? money(policy.min_income) : "—"}</strong></div>
        <div><span className="muted">Max loan</span><br /><strong>{policy.max_loan_amount ? money(policy.max_loan_amount) : "—"}</strong></div>
        <div><span className="muted">ROI</span><br /><strong>{policy.roi_min}% – {policy.roi_max}%</strong></div>
        <div><span className="muted">Tenure</span><br /><strong>{policy.tenure_min_months} – {policy.tenure_max_months} mo</strong></div>
        <div><span className="muted">Age</span><br /><strong>{policy.min_age} – {policy.max_age} yrs</strong></div>
        <div><span className="muted">Employment</span><br /><strong>{(policy.employment_types || []).join(", ") || "Any"}</strong></div>
      </div>

      {policy.eligibility_notes && (
        <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }} data-testid={`policy-card-notes-${policy.policy_id}`}>
          {policy.eligibility_notes}
        </p>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button
          className="outline-button"
          data-testid={`policy-card-toggle-${policy.policy_id}`}
          onClick={loadDetail}
          style={{ fontSize: 12, padding: "6px 10px" }}
        >
          <FileText size={14} /> {expanded ? "Hide details" : "Docs & EMI"}
        </button>
      </div>

      {expanded && detail && (
        <div style={{ borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 10 }}>
          <strong style={{ fontSize: 12 }}>Required documents (from {detail.bank?.name})</strong>
          {(detail.required_documents || []).length === 0 ? (
            <p className="muted" style={{ fontSize: 12 }} data-testid={`policy-docs-empty-${policy.policy_id}`}>
              No documents configured — check with Admin.
            </p>
          ) : (
            <ul style={{ margin: "6px 0 12px 18px", fontSize: 12 }} data-testid={`policy-docs-${policy.policy_id}`}>
              {(detail.required_documents || []).map((d, i) => (
                <li key={i}>
                  {d.name}{d.mandatory ? " *" : ""}
                  {d.notes ? <span className="muted"> — {d.notes}</span> : null}
                </li>
              ))}
            </ul>
          )}
          <InlineEmiCalc initialRoi={midRoi} initialTenure={midTenure} testIdPrefix={`policy-emi-${policy.policy_id}`} />
        </div>
      )}
    </div>
  );
}

export function InlineEmiCalc({ initialPrincipal = 500000, initialRoi = 12, initialTenure = 60, testIdPrefix = "emi" }) {
  const [principal, setPrincipal] = useState(initialPrincipal);
  const [roi, setRoi] = useState(initialRoi);
  const [tenure, setTenure] = useState(initialTenure);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const calc = async () => {
    setBusy(true);
    try {
      const r = await http.post("/emi/calculate", {
        principal: Number(principal) || 0,
        annual_rate: Number(roi) || 0,
        tenure_months: Number(tenure) || 0,
      });
      setResult(r.data);
    } catch (err) {
      notify.error(err, "EMI calculation failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // Auto-calc once on mount
    calc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid={`${testIdPrefix}-widget`} style={{ background: "var(--surface-alt, #f7f7f9)", border: "1px solid var(--border)", padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <Calculator size={14} /> <strong style={{ fontSize: 12 }}>EMI Calculator</strong>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, alignItems: "end", fontSize: 11 }}>
        <label style={{ fontSize: 11 }}>
          Principal (₹)
          <input
            type="number" min="0" step="10000" value={principal}
            data-testid={`${testIdPrefix}-principal`}
            onChange={(e) => setPrincipal(e.target.value)}
            style={{ fontSize: 12, width: "100%" }}
          />
        </label>
        <label style={{ fontSize: 11 }}>
          ROI (%)
          <input
            type="number" min="0" step="0.05" value={roi}
            data-testid={`${testIdPrefix}-roi`}
            onChange={(e) => setRoi(e.target.value)}
            style={{ fontSize: 12, width: "100%" }}
          />
        </label>
        <label style={{ fontSize: 11 }}>
          Tenure (mo)
          <input
            type="number" min="1" value={tenure}
            data-testid={`${testIdPrefix}-tenure`}
            onChange={(e) => setTenure(e.target.value)}
            style={{ fontSize: 12, width: "100%" }}
          />
        </label>
      </div>
      <button
        type="button"
        className="primary-button"
        disabled={busy}
        data-testid={`${testIdPrefix}-calc`}
        onClick={calc}
        style={{ fontSize: 12, padding: "6px 10px", marginTop: 8, width: "100%" }}
      >
        {busy ? "Calculating…" : "Calculate EMI"}
      </button>
      {result && (
        <div
          data-testid={`${testIdPrefix}-result`}
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 8, fontSize: 12 }}
        >
          <div><span className="muted">EMI</span><br /><strong data-testid={`${testIdPrefix}-emi`}>{money(result.emi)}</strong></div>
          <div><span className="muted">Total interest</span><br /><strong>{money(result.total_interest)}</strong></div>
          <div><span className="muted">Total payable</span><br /><strong>{money(result.total_payable)}</strong></div>
        </div>
      )}
    </div>
  );
}
